import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Tables, TablesInsert } from '@/integrations/supabase/types';

export type RentalBooking = Tables<'rental_bookings'>;
export type RentalBookingInsert = Omit<TablesInsert<'rental_bookings'>, 'id' | 'created_at' | 'updated_at' | 'booking_number'> & { booking_number?: string };

export const useRentalBookings = () => {
  return useQuery({
    queryKey: ['rental-bookings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rental_bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as RentalBooking[];
    },
  });
};

export const useCreateRentalBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (booking: RentalBookingInsert) => {
      const { data, error } = await supabase
        .from('rental_bookings')
        .insert([booking as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-bookings'] });
      toast.success('Booking created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create booking');
      console.error('Error creating booking:', error);
    },
  });
};

export const useUpdateRentalBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<RentalBooking> }) => {
      const { data, error } = await supabase
        .from('rental_bookings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-bookings'] });
      toast.success('Booking updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update booking');
      console.error('Error updating booking:', error);
    },
  });
};

export const useDeleteRentalBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('rental_bookings')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-bookings'] });
      toast.success('Booking deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete booking');
      console.error('Error deleting booking:', error);
    },
  });
};

export const calculateRentalPrice = (
  dailyRate: number,
  weeklyRate: number | null,
  monthlyRate: number | null,
  days: number,
  insuranceCost: number,
  extrasCost: number
) => {
  let baseCost = 0;

  // Calculate base cost based on rental duration
  if (days >= 30 && monthlyRate) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    baseCost = months * monthlyRate + remainingDays * dailyRate;
  } else if (days >= 7 && weeklyRate) {
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    baseCost = weeks * weeklyRate + remainingDays * dailyRate;
  } else {
    baseCost = days * dailyRate;
  }

  return baseCost + insuranceCost + extrasCost;
};